import type { AxiosResponse } from 'axios';
import { type DefaultError, useMutation } from '@tanstack/react-query';
import { useToast } from '@eqi-investimentos/react';
import { captureException } from '@sentry/nextjs';
import { EQIAdvisor } from '@/modules/Customer/interface/Advisor';
import { api } from '@/libs/axios/api';
import { isBusinessHour } from '@/libs/salesForce/scripts/SFChatScript';
import { openChatWithOpportunity } from '@/libs/salesForce/utils/openChatWithOpportunity';
import { apiConfig } from '@/config/api';
import { appConfig } from '@/config/app';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { sanitizeNumber } from '@/utils/format';
import { formatAdvisorWhatsAppLink } from '@/utils/messenger';
import {
  formatOpportunityQuestionsAndAnswers,
  formatOpportunityDescription,
} from '@/utils/string';
import { useGetCustomerAdvisor } from '../useGetCustomerAdvisor';

export const useCreateSFOpportunity = () => {
  const toast = useToast({
    variant: 'success',
    position: 'top',
    showIcon: false,
  });
  const { data } = useGetCustomerStatus();
  const { data: advisor } = useGetCustomerAdvisor();

  return useMutation<void, DefaultError, Opportunity | RawOpportunity>({
    mutationFn: (opportunity) =>
      createSFOpportunity({
        opportunity,
        isEQIDigital: !data?.isEqi || data?.isEqiDigital,
        toast,
        advisor,
      }),
    onError: (error, variables) => {
      captureException({
        tag: 'Salesforce.LevantadaDeMao',
        error,
        data: variables,
        message: 'Erro ao efetuar levantada de mão',
      });
    },
  });
};

const createSFOpportunity = async ({
  isEQIDigital = true,
  opportunity,
  toast,
  advisor,
}: CreateSFOpportunityParams) => {
  if (isEQIDigital) {
    const questionsAndAnswers =
      'rawQuestions' in opportunity
        ? formatOpportunityQuestionsAndAnswers(
            opportunity.rawQuestions ?? {},
            opportunity.translate
          )
        : {};
    if (!isBusinessHour()) {
      if (advisor) {
        const message = `Olá ${advisor?.advisorName.split(' ')[0]}, estou no EQI+ e gostaria de tirar uma dúvida `;

        const url = formatAdvisorWhatsAppLink({
          message: message,
          number: sanitizeNumber(advisor?.advisorMobilePhone ?? ''),
        });

        window.open(url, '_blank');
        return;
      } else {
        window.open(
          `${appConfig.support.whatsAppUrlBase}?phone=${appConfig.support.number}&text=${appConfig.support.message}`
        );
        return;
      }
    }
    openChatWithOpportunity({
      action: opportunity.action,
      amount: opportunity.amount,
      product: opportunity.product,
      ...questionsAndAnswers,
    });
    return;
  }
  const description = getDescriptionFromOpportunity(opportunity);

  try {
    await api.post<void, AxiosResponse<void>, Opportunity>(
      apiConfig.routes.salesForce.customerAction,
      {
        action: opportunity.action,
        amount: opportunity.amount,
        product: opportunity.product,
        ...(description && { description }),
        ...(!description && { metadata: opportunity.action }),
        ...(opportunity.campaign_id && {
          campaign_id: opportunity.campaign_id,
        }),
      }
    );

    toast({
      description:
        'Seu interesse foi registrado, em breve seu assessor entrará em contato!',
      status: 'warning',
    });
  } catch {
    toast({
      description: 'Ocorreu um erro, por favor, tente novamente mais tarde',
      status: 'error',
    });
  }
};

const getDescriptionFromOpportunity = (
  opportunity: CreateSFOpportunityParams['opportunity']
) => {
  if ('rawQuestions' in opportunity && opportunity.rawQuestions) {
    return formatOpportunityDescription(
      opportunity.rawQuestions,
      opportunity.translate
    );
  }
  if ('description' in opportunity) {
    return opportunity.description;
  }
  return '';
};

type CreateSFOpportunityParams = {
  opportunity: Opportunity | RawOpportunity;
  isEQIDigital?: boolean;
  toast: ReturnType<typeof useToast>;
  advisor: EQIAdvisor | undefined;
};

export type Opportunity = {
  action: string;
  product?: string;
  amount?: string | number;
  description?: string;
  campaign_id?: string;
};

type RawOpportunity = Omit<Opportunity, 'description'> & {
  rawQuestions?: Record<string, string | string[]>;
  translate?: (id: string) => string;
};
